import {
  Badge,
  Banner,
  Button,
  Empty,
  Grid,
  GridItem,
  Loader,
  Surface,
  Table,
  Tabs,
  Text,
  TimeseriesChart,
} from '@cloudflare/kumo'
import { ArrowsClockwise, ChartLineUp } from '@phosphor-icons/react'
import {
  SIM_FAILURE_CODE_LABELS,
  type AdminFailureStats,
  type AdminStats,
  type SimFailureCode,
} from '@repo/shared'
import * as echarts from 'echarts'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { adminApi } from '../api'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import { PaymentSourceBadge } from '../components/payment-source-badge'
import { useGetToken } from '../lib/auth'
import { fmtInt, fmtUsd, toMessage } from '../lib/format'

const RANGES = [7, 30, 90] as const

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'warn' | 'bad'
}) {
  return (
    <Surface className="flex h-full flex-col gap-1 rounded-lg p-5">
      <Text variant="secondary" size="sm">
        {label}
      </Text>
      <div
        className={`text-2xl font-semibold tabular-nums ${
          tone === 'bad' ? 'text-kumo-danger' : tone === 'warn' ? 'text-kumo-warning' : ''
        }`}
      >
        {value}
      </div>
      {hint && (
        <Text variant="secondary" size="xs">
          {hint}
        </Text>
      )}
    </Surface>
  )
}

const pct = (n: number, d: number) => (d > 0 ? `${Math.round((n / d) * 1000) / 10}%` : '—')

/** 기능 이름이 비어 있으면(2026-10-11 전 기록) 표에서 구분해 보이게 */
const purposeLabel = (p: string) => p || '(기록 전)'

/**
 * 기능별 AI 비용 한 행의 비용 확인 건수. n 에는 usd 를 모르는(null) 호출도 들어가므로 호출당 비용은 usd / costed 로 낸다 —
 * usd / n 이면 미확인 호출만큼 싸게 보이고, 미확인뿐인 기능은 $0 으로 공짜처럼 읽힌다 (2026-10-16).
 * costed 를 안 보내는 옛 서버면 전부 확인된 것으로 본다 (그전 표와 같은 계산)
 */
const costedOf = (p: { n: number; costed?: number }) => p.costed ?? p.n

/** 실패 단계 이름 (internal.failure.stage). 기록 전 실패는 '' */
const STAGE_LABELS: Record<string, string> = {
  prepare: '준비',
  core: '초안(core)',
  detail: '상세·시세·심사',
  gate: '게이트 판정',
  'draft-save': '1차안 저장',
  save: '결과 저장',
  photos: '사진·비교 제품',
  notify: '완성 알림',
  unknown: '알 수 없음',
}
const stageLabel = (s: string) => (s ? (STAGE_LABELS[s] ?? s) : '(기록 전)')
const codeLabel = (c: string) =>
  c ? (SIM_FAILURE_CODE_LABELS[c as SimFailureCode] ?? c) : '(기록 전)'
const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: '웹',
  other: '기타',
  legacy: '옛 앱(헤더 없음)',
  admin: '관리자 재실행',
  unknown: '(기록 전)',
}
const platformLabel = (p: string) => PLATFORM_LABELS[p] ?? p

/**
 * 실패 분석 표 (2026-10-17) — 실패 단계·사유별 / 플랫폼·앱 버전별 중 하나를 고르는 작은 표 하나와 멈춘 생성·사진 실패 요약.
 * 옛 서버는 failures 를 보내지 않으므로 그때는 그리지 않는다
 */
function FailureTable({ f }: { f: AdminFailureStats }) {
  const [view, setView] = useState<'reason' | 'client'>('reason')
  const stuck = f.stuck.running + f.stuck.queued
  return (
    <Surface className="flex flex-col gap-3 rounded-lg p-0">
      <div className="flex flex-col gap-2 px-5 pt-5">
        <Text variant="heading">실패 분석</Text>
        <Text variant="secondary" size="xs">
          멈춘 생성(진행 중인데 {f.stuck.minutes}분 넘게 그대로){' '}
          <span className={stuck > 0 ? 'text-kumo-danger font-semibold' : ''}>
            {fmtInt(stuck)}건
          </span>{' '}
          (running {fmtInt(f.stuck.running)} · queued {fmtInt(f.stuck.queued)}) · 사진 실패율{' '}
          {pct(f.photos.failed, f.photos.total)} ({fmtInt(f.photos.failed)} /{' '}
          {fmtInt(f.photos.total)}
          건, 일부 컷만 실패 {fmtInt(f.photos.partial)}건)
        </Text>
        <Tabs
          variant="segmented"
          size="sm"
          value={view}
          onValueChange={(v) => setView(v === 'client' ? 'client' : 'reason')}
          tabs={[
            { value: 'reason', label: '단계·사유별' },
            { value: 'client', label: '플랫폼·버전별' },
          ]}
        />
      </div>
      <div className="overflow-x-auto">
        {view === 'reason' ? (
          f.byReason.length === 0 ? (
            <div className="px-5 pb-5">
              <Text variant="secondary" size="sm">
                이 기간에 실패한 생성이 없어요.
              </Text>
            </div>
          ) : (
            <Table>
              <Table.Header variant="compact">
                <Table.Row>
                  <Table.Head>단계</Table.Head>
                  <Table.Head>사유</Table.Head>
                  <Table.Head>실패</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {f.byReason.map((r) => (
                  <Table.Row key={`${r.stage}|${r.code}`}>
                    <Table.Cell>{stageLabel(r.stage)}</Table.Cell>
                    <Table.Cell>{codeLabel(r.code)}</Table.Cell>
                    <Table.Cell className="tabular-nums">{fmtInt(r.n)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )
        ) : f.byClient.length === 0 ? (
          <div className="px-5 pb-5">
            <Text variant="secondary" size="sm">
              이 기간에 생성이 없어요.
            </Text>
          </div>
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>플랫폼</Table.Head>
                <Table.Head>앱 버전</Table.Head>
                <Table.Head>생성</Table.Head>
                <Table.Head>실패</Table.Head>
                <Table.Head>실패율</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {f.byClient.map((r) => (
                <Table.Row key={`${r.platform}|${r.version ?? ''}`}>
                  <Table.Cell>{platformLabel(r.platform)}</Table.Cell>
                  <Table.Cell className="font-mono text-xs">{r.version ?? '—'}</Table.Cell>
                  <Table.Cell className="tabular-nums">{fmtInt(r.generations)}</Table.Cell>
                  <Table.Cell className="tabular-nums">{fmtInt(r.failed)}</Table.Cell>
                  <Table.Cell className="tabular-nums">{pct(r.failed, r.generations)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </Surface>
  )
}

/**
 * 대시보드 — 생성 건수·실패율·best-of 비율·AI 비용(실측)·플랜 분포·한도 임박 사용자·견적 파이프라인.
 * 가격 스펙의 "Pro 60회는 첫 달 사용률을 보고 조정" 판단 근거가 여기서 나온다.
 */
export function DashboardPage() {
  const getToken = useGetToken()
  const navigate = useNavigate()
  const [days, setDays] = useState<(typeof RANGES)[number]>(30)
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    setBusy(true)
    try {
      setStats(await adminApi.stats(getToken, days))
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }, [getToken, days])

  useEffect(() => {
    void reload()
  }, [reload])

  const t = stats?.totals
  const ts = (d: string) => new Date(`${d}T00:00:00`).getTime()

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="대시보드"
        description="생성 품질과 AI 비용, 플랜 사용량, 견적 진행 상황을 한눈에 봐요."
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={busy}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />

      <HelpPanel
        id="dashboard"
        steps={[
          <>위 탭에서 기간(7·30·90일)을 고르세요. 모든 숫자가 그 기간 기준으로 바뀌어요.</>,
          <>
            <b>실패율</b>이 5%를 넘거나 <b>best-of 비율</b>(게이트를 3번 다 못 넘고 나간 결과)이
            20%를 넘으면 [시뮬레이션] 에서 "best-of 만" 필터로 어떤 검사에서 떨어지는지 보세요.
          </>,
          <>
            <b>제품당 AI 비용</b>이 가격 스펙의 가정($0.30)보다 크면 Pro 한도(월 60회)나 사진 재생성
            한도를 [설정] 에서 낮추는 걸 검토하세요.
          </>,
          <>
            <b>한도 임박 사용자</b>는 이번 달 생성을 80% 이상 쓴 유료 사용자예요. 많아지면
            업그레이드 여지가 있다는 신호예요.
          </>,
        ]}
        notes={[
          '실제 청구액 기준이에요. 미확인 건은 빠져 있어요.',
          '비용 기록은 2026-09-19 배포 이후 생성분부터 쌓여요. 기능 이름(purpose)은 2026-10-11 부터 남아요.',
          '기능별 비용 표: 가벼운 일(번역·교정)은 작은 모델, 기획·심사는 큰 모델, 시세 검색은 웹 검색 모델로 나가요. 한 기능에 두 모델이 보이면 작은 모델이 실패해 다시 부른 호출이에요.',
          '"비용 미확인"은 호출은 있었지만 청구액이 오지 않은 건수예요(스트림이 중간에 끊긴 시도 등). USD·비중에는 들어가지 않고, 호출당은 비용을 아는 호출만으로 나눠요. 미확인이 많은 기능은 실제 비용이 표보다 커요.',
          '실패 분석 표: 단계·사유와 플랫폼·앱 버전은 2026-10-17 서버 배포 뒤 생성분부터 남아요. 그 전 것은 "(기록 전)"으로 보여요. "옛 앱(헤더 없음)"은 버전 헤더를 보내지 않는 1.0.3 이전 설치본이에요.',
        ]}
      />

      <Tabs
        variant="segmented"
        size="sm"
        value={String(days)}
        onValueChange={(v) => setDays(Number(v) as (typeof RANGES)[number])}
        tabs={RANGES.map((d) => ({ value: String(d), label: `최근 ${d}일` }))}
      />

      {error && <Banner variant="error" title="불러오지 못했어요" description={error} />}

      {!stats || !t ? (
        <Surface className="flex items-center gap-3 rounded-lg p-6">
          <Loader />
          <Text variant="secondary">불러오는 중…</Text>
        </Surface>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <div>
              <Stat
                label="생성"
                value={fmtInt(t.generations)}
                hint={`평균 ${t.avgAttempts}회 시도`}
              />
            </div>
            <div>
              <Stat
                label="실패율"
                value={pct(t.failed, t.generations)}
                hint={`${fmtInt(t.failed)}건 실패`}
                tone={t.generations > 0 && t.failed / t.generations > 0.05 ? 'bad' : undefined}
              />
            </div>
            <div>
              <Stat
                label="best-of 비율"
                value={pct(t.bestOf, t.generations)}
                hint="게이트 미통과로 나간 결과"
                tone={t.generations > 0 && t.bestOf / t.generations > 0.2 ? 'warn' : undefined}
              />
            </div>
            <div>
              <Stat
                label="제품당 AI 비용"
                value={t.usdPerGeneration === null ? '—' : fmtUsd(t.usdPerGeneration, 3)}
                hint={`합계 ${fmtUsd(t.usd)} · 가정 $0.30`}
                tone={t.usdPerGeneration !== null && t.usdPerGeneration > 0.3 ? 'warn' : undefined}
              />
            </div>
          </div>

          <Surface className="flex flex-col gap-3 rounded-lg p-5">
            <Text variant="heading">일별 생성</Text>
            {t.generations === 0 ? (
              <Empty
                icon={<ChartLineUp size={40} className="text-kumo-inactive" />}
                title="이 기간에 생성이 없어요"
                description="앱에서 제품을 만들면 여기에 쌓여요."
              />
            ) : (
              <TimeseriesChart
                echarts={echarts}
                type="bar"
                height={260}
                data={[
                  {
                    name: '생성',
                    color: '#3b82f6',
                    data: stats.daily.map((d) => [ts(d.date), d.generations]),
                  },
                  {
                    name: 'best-of',
                    color: '#f59e0b',
                    data: stats.daily.map((d) => [ts(d.date), d.bestOf]),
                  },
                  {
                    name: '실패',
                    color: '#ef4444',
                    data: stats.daily.map((d) => [ts(d.date), d.failed]),
                  },
                ]}
              />
            )}
          </Surface>

          {stats.failures && <FailureTable f={stats.failures} />}

          <Grid variant="2up" gap="sm">
            <GridItem>
              <Surface className="flex h-full flex-col gap-3 rounded-lg p-5">
                <Text variant="heading">AI 비용 (USD)</Text>
                {t.usd === 0 ? (
                  <Text variant="secondary" size="sm">
                    아직 기록된 비용이 없어요.
                  </Text>
                ) : (
                  <TimeseriesChart
                    echarts={echarts}
                    type="line"
                    height={200}
                    data={[
                      {
                        name: 'USD',
                        color: '#8b5cf6',
                        data: stats.daily.map((d) => [ts(d.date), d.usd]),
                      },
                    ]}
                  />
                )}
                <Text variant="secondary" size="xs">
                  호출 {fmtInt(t.totalCalls)}건 중 비용 확인 {fmtInt(t.costedCalls)}건 · 이미지{' '}
                  {fmtInt(t.images)}장 · 웹 검색 {fmtInt(t.searches)}회
                </Text>
              </Surface>
            </GridItem>
            <GridItem>
              <Surface className="flex h-full flex-col gap-4 rounded-lg p-5">
                <Text variant="heading">견적 진행</Text>
                <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  {(
                    [
                      ['견적 대기', stats.quotes.submitted],
                      ['견적 보냄', stats.quotes.quoted],
                      ['공장 선택됨', stats.quotes.accepted],
                      ['마감', stats.quotes.closed],
                    ] as const
                  ).map(([label, n]) => (
                    <div key={label} className="flex flex-col">
                      <span className="text-kumo-subtle text-xs">{label}</span>
                      <span className="text-xl font-semibold tabular-nums">{fmtInt(n)}</span>
                    </div>
                  ))}
                </div>
                {stats.quotes.overdue > 0 ? (
                  <Banner
                    variant="error"
                    size="sm"
                    title={`지연 ${stats.quotes.overdue}건`}
                    description="3일 넘게 견적이 없는 요청이 있어요."
                  />
                ) : (
                  <Text variant="secondary" size="sm">
                    지연된 요청이 없어요.
                  </Text>
                )}
                <div>
                  <Button variant="secondary" onClick={() => navigate('/quotes')}>
                    견적 요청 열기
                  </Button>
                </div>
              </Surface>
            </GridItem>
          </Grid>

          <Surface className="flex flex-col gap-3 rounded-lg p-0">
            <div className="px-5 pt-5">
              <Text variant="heading">기능별 AI 비용</Text>
              <Text variant="secondary" size="xs">
                어느 기능에서 돈이 나가는지 — 기능(바깥/안)과 모델로 묶은 이번 기간 합계, 비용 큰
                순. 비중은 이번 기간 AI 비용 합계 대비, 호출당은 비용을 아는 호출만으로 나눈
                값이에요.
              </Text>
            </div>
            {!stats.purposes || stats.purposes.length === 0 ? (
              <div className="px-5 pb-5">
                <Text variant="secondary" size="sm">
                  아직 기록된 호출이 없어요.
                </Text>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <Table.Header variant="compact">
                    <Table.Row>
                      <Table.Head>기능</Table.Head>
                      <Table.Head>모델</Table.Head>
                      <Table.Head>호출</Table.Head>
                      <Table.Head>USD</Table.Head>
                      <Table.Head>호출당</Table.Head>
                      <Table.Head>비중</Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {stats.purposes.map((p) => {
                      const costed = costedOf(p)
                      const unknown = p.n - costed
                      return (
                        <Table.Row key={`${p.purpose}|${p.model}`}>
                          <Table.Cell className="font-mono text-xs">
                            {purposeLabel(p.purpose)}
                          </Table.Cell>
                          <Table.Cell className="font-mono text-xs">{p.model}</Table.Cell>
                          <Table.Cell className="tabular-nums">
                            {fmtInt(p.n)}
                            {unknown > 0 && (
                              <span className="text-kumo-warning block text-xs">
                                비용 미확인 {fmtInt(unknown)}건
                              </span>
                            )}
                          </Table.Cell>
                          <Table.Cell className="tabular-nums">
                            {costed > 0 ? fmtUsd(p.usd, 4) : '미확인'}
                          </Table.Cell>
                          <Table.Cell className="tabular-nums">
                            {costed > 0 ? fmtUsd(p.usd / costed, 4) : '—'}
                          </Table.Cell>
                          <Table.Cell className="tabular-nums">
                            {costed > 0 ? pct(p.usd, t.usd) : '—'}
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table>
              </div>
            )}
          </Surface>

          <Grid variant="2up" gap="sm">
            <GridItem>
              <Surface className="flex h-full flex-col gap-3 rounded-lg p-5">
                <Text variant="heading">유료 플랜</Text>
                <div className="flex flex-wrap gap-6 text-sm">
                  <div className="flex flex-col">
                    <span className="text-kumo-subtle text-xs">Basic</span>
                    <span className="text-xl font-semibold tabular-nums">
                      {fmtInt(stats.plans.basic)}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-kumo-subtle text-xs">Pro</span>
                    <span className="text-xl font-semibold tabular-nums">
                      {fmtInt(stats.plans.pro)}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-kumo-subtle text-xs">그중 체험 중</span>
                    <span className="text-xl font-semibold tabular-nums">
                      {fmtInt(stats.plans.trial)}
                    </span>
                  </div>
                </div>
                <Text variant="secondary" size="xs">
                  RevenueCat 이 알려준 구독 기준이에요. 매출·해지율은 RevenueCat 대시보드에서
                  보세요.
                </Text>
                {stats.sandboxPlans && (
                  <Text variant="secondary" size="xs">
                    테스터(샌드박스) 구독은 위 숫자에서 빼고 따로 셌어요 — Basic{' '}
                    {fmtInt(stats.sandboxPlans.basic)} · Pro {fmtInt(stats.sandboxPlans.pro)} · 체험{' '}
                    {fmtInt(stats.sandboxPlans.trial)}
                  </Text>
                )}
              </Surface>
            </GridItem>
            <GridItem>
              <Surface className="flex h-full flex-col gap-3 rounded-lg p-0">
                <div className="px-5 pt-5">
                  <Text variant="heading">한도 임박 사용자</Text>
                  <Text variant="secondary" size="xs">
                    이번 달 생성을 80% 이상 쓴 유료 사용자
                  </Text>
                </div>
                {stats.nearLimit.length === 0 ? (
                  <div className="px-5 pb-5">
                    <Text variant="secondary" size="sm">
                      아직 없어요.
                    </Text>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <Table.Header variant="compact">
                        <Table.Row>
                          <Table.Head>사용자</Table.Head>
                          <Table.Head>플랜</Table.Head>
                          <Table.Head>사용</Table.Head>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {stats.nearLimit.slice(0, 10).map((u) => (
                          <Table.Row key={u.userId}>
                            <Table.Cell>
                              <button
                                type="button"
                                className="text-kumo-link font-mono text-xs hover:underline"
                                onClick={() =>
                                  navigate(`/users?id=${encodeURIComponent(u.userId)}`)
                                }
                              >
                                {u.userId}
                              </button>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge variant={u.plan === 'pro' ? 'purple' : 'blue'}>{u.plan}</Badge>
                              {u.isSandbox && <PaymentSourceBadge source="sandbox" />}
                            </Table.Cell>
                            <Table.Cell className="tabular-nums">
                              {u.used} / {u.limit}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                )}
              </Surface>
            </GridItem>
            {stats.topCostUsers && (
              <GridItem>
                <Surface className="flex h-full flex-col gap-3 rounded-lg p-0">
                  <div className="px-5 pt-5">
                    <Text variant="heading">AI 비용 상위 사용자</Text>
                    <Text variant="secondary" size="xs">
                      이번 기간 AI 호출 비용이 큰 로그인 사용자 (손님·생성 밖 호출 제외)
                    </Text>
                  </div>
                  {stats.topCostUsers.length === 0 ? (
                    <div className="px-5 pb-5">
                      <Text variant="secondary" size="sm">
                        아직 없어요.
                      </Text>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <Table.Header variant="compact">
                          <Table.Row>
                            <Table.Head>사용자</Table.Head>
                            <Table.Head>비용</Table.Head>
                            <Table.Head>생성 · 호출</Table.Head>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {stats.topCostUsers.map((u) => (
                            <Table.Row key={u.userId}>
                              <Table.Cell>
                                <button
                                  type="button"
                                  className="text-kumo-link font-mono text-xs hover:underline"
                                  onClick={() =>
                                    navigate(`/users?id=${encodeURIComponent(u.userId)}`)
                                  }
                                >
                                  {u.userId}
                                </button>
                              </Table.Cell>
                              <Table.Cell className="tabular-nums">{fmtUsd(u.usd, 4)}</Table.Cell>
                              <Table.Cell className="tabular-nums">
                                {fmtInt(u.generations)} · {fmtInt(u.calls)}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                  )}
                </Surface>
              </GridItem>
            )}
          </Grid>
        </>
      )}
    </div>
  )
}
